import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Hero } from '../components/Hero';
import { FeatureShowcase } from '../components/FeatureShowcase';
import { Features } from '../components/Features';
import { Metrics } from '../components/Metrics';
import { GetStarted } from '../components/GetStarted';
import { FAQ } from '../components/FAQ';
import { Footer } from '../components/Footer';
export function Home() {
  const [pulseSize, setPulseSize] = useState(100);
  const [pulseOpacity, setPulseOpacity] = useState(0.1);
  const [neuronActive, setNeuronActive] = useState<number[]>([]);
  const [scrollY, setScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState(0);
  const [circuitNodes, setCircuitNodes] = useState<{
    top: number;
    active: boolean;
  }[]>([]);
  const [aiPulseIntensity, setAiPulseIntensity] = useState(0.6);
  const [aiPulseSize, setAiPulseSize] = useState(100);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  // AI pulse animation effect
  useEffect(() => {
    const aiPulseInterval = setInterval(() => {
      setAiPulseIntensity(prev => prev === 0.6 ? 0.8 : 0.6);
      setAiPulseSize(prev => prev === 100 ? 110 : 100);
    }, 1500);
    return () => clearInterval(aiPulseInterval);
  }, []);
  // Brain wave animation effect
  useEffect(() => {
    const pulsateInterval = setInterval(() => {
      setPulseSize(prev => prev === 100 ? 120 : 100);
      setPulseOpacity(prev => prev === 0.1 ? 0.15 : 0.1);
    }, 2000);
    return () => clearInterval(pulsateInterval);
  }, []);
  // Random neuron activation effect
  useEffect(() => {
    const activateNeuron = () => {
      const newNeuron = Math.floor(Math.random() * 15);
      setNeuronActive(prev => [...prev, newNeuron]);
      setTimeout(() => {
        setNeuronActive(prev => prev.filter(n => n !== newNeuron));
      }, 1500);
    };
    const neuronInterval = setInterval(activateNeuron, 800);
    return () => clearInterval(neuronInterval);
  }, []);
  // Scroll effect and section tracking
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      // Find the current active section based on scroll position
      const viewportMiddle = window.scrollY + window.innerHeight / 2;
      let newActiveSection = 0;
      sectionRefs.current.forEach((section, index) => {
        if (section) {
          const {
            top,
            bottom
          } = section.getBoundingClientRect();
          const sectionTop = top + window.scrollY;
          const sectionBottom = bottom + window.scrollY;
          if (viewportMiddle >= sectionTop && viewportMiddle <= sectionBottom) {
            newActiveSection = index;
          }
        }
      });
      if (newActiveSection !== activeSection) {
        setActiveSection(newActiveSection);
        // Update circuit nodes
        setCircuitNodes(prev => prev.map((node, index) => ({
          ...node,
          active: index === newActiveSection
        })));
        // Create blast animation
        setTimeout(() => {
          setCircuitNodes(prev => prev.map((node, index) => ({
            ...node,
            active: index === newActiveSection ? true : false
          })));
        }, 300);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSection]);
  // Initialize circuit nodes based on section positions
  useEffect(() => {
    // Wait for sections to be rendered
    if (sectionRefs.current.filter(Boolean).length > 0) {
      const nodes = sectionRefs.current.map((section, index) => {
        if (!section) return {
          top: 0,
          active: false
        };
        const sectionMiddle = section.offsetTop + section.offsetHeight / 2;
        return {
          top: sectionMiddle,
          active: index === 0 // Initially activate the first node
        };
      });
      setCircuitNodes(nodes);
    }
  }, []);
  return <div className="w-full min-h-screen bg-[#1E2A38] text-[#F5F7FA] font-sans relative overflow-hidden" data-id="element-2442">
      {/* Futuristic AI Pulse Effect */}
      <div className="absolute top-0 left-0 w-full h-[90vh] overflow-hidden z-0 pointer-events-none" data-id="element-2443">
        {/* Main AI Pulse Glow */}
        <div className="absolute left-1/2 top-48 transform -translate-x-1/2 rounded-full" style={{
        width: `${aiPulseSize}%`,
        height: `${aiPulseSize * 0.5}%`,
        background: 'radial-gradient(circle, rgba(200,241,105,0.25) 0%, rgba(59,130,246,0.15) 40%, rgba(30,42,56,0) 70%)',
        opacity: aiPulseIntensity,
        filter: 'blur(60px)',
        transition: 'all 1.5s ease-in-out'
      }} data-id="element-2444"></div>
        {/* Secondary Pulse Elements */}
        <div className="absolute left-1/4 top-72 w-32 h-32 rounded-full" style={{
        background: 'radial-gradient(circle, rgba(200,241,105,0.2) 0%, rgba(30,42,56,0) 70%)',
        opacity: aiPulseIntensity * 0.7,
        filter: 'blur(20px)',
        animation: 'float 8s infinite ease-in-out'
      }} data-id="element-2445"></div>
        <div className="absolute right-1/4 top-64 w-48 h-48 rounded-full" style={{
        background: 'radial-gradient(circle, rgba(59,130,246,0.2) 0%, rgba(30,42,56,0) 70%)',
        opacity: aiPulseIntensity * 0.8,
        filter: 'blur(25px)',
        animation: 'float 10s infinite ease-in-out reverse',
        animationDelay: '2s'
      }} data-id="element-2446"></div>
        {/* Digital Circuit Lines */}
        <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" data-id="element-2447">
          <defs data-id="element-2448">
            <linearGradient id="circuitGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-2449">
              <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.8" data-id="element-2450" />
              <stop offset="100%" stopColor="#C8F169" stopOpacity="0.8" data-id="element-2451" />
            </linearGradient>
          </defs>
          <path d="M0,100 C100,180 300,50 400,120 C500,190 700,60 800,100 C900,140 1000,80 1200,100" stroke="url(#circuitGradient)" strokeWidth="0.5" fill="none" strokeDasharray="5,5" style={{
          opacity: aiPulseIntensity,
          transition: 'opacity 1.5s ease-in-out'
        }} data-id="element-2452" />
          <path d="M0,150 C200,100 400,200 600,150 C800,100 1000,200 1200,150" stroke="url(#circuitGradient)" strokeWidth="0.5" fill="none" strokeDasharray="5,5" style={{
          opacity: aiPulseIntensity * 0.8,
          transition: 'opacity 1.5s ease-in-out'
        }} data-id="element-2453" />
        </svg>
        {/* Digital Nodes */}
        {Array.from({
        length: 8
      }).map((_, i) => <div key={`ai-node-${i}`} className="absolute rounded-full bg-[#C8F169]" style={{
        width: '3px',
        height: '3px',
        left: `${15 + i * 10}%`,
        top: `${20 + i % 3 * 10}%`,
        opacity: i % 2 === 0 ? aiPulseIntensity : aiPulseIntensity * 0.6,
        boxShadow: '0 0 8px rgba(200,241,105,0.8)',
        transition: 'opacity 0.5s ease-in-out'
      }} data-id="element-2454"></div>)}
      </div>
      {/* Brain Wave Glow Effect */}
      <div className="absolute top-0 left-0 w-full overflow-hidden z-0 pointer-events-none" data-id="element-2455">
        <svg className="w-full h-32 absolute top-24" viewBox="0 0 1200 120" preserveAspectRatio="none" data-id="element-2456">
          <defs data-id="element-2457">
            <linearGradient id="brainWaveGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-2458">
              <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" data-id="element-2459" />
              <stop offset="50%" stopColor="#C8F169" stopOpacity="0.5" data-id="element-2460" />
              <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.3" data-id="element-2461" />
            </linearGradient>
            <filter id="glow" x="-20%" y="-20%" width="140%" height="140%" data-id="element-2462">
              <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" data-id="element-2463" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" data-id="element-2464" />
            </filter>
          </defs>
          <path d="M0,60 C200,100 300,20 400,60 C500,100 600,20 700,60 C800,100 900,20 1000,60 C1100,100 1200,20 1200,60 V120 H0 Z" fill="url(#brainWaveGradient)" style={{
          filter: 'url(#glow)',
          opacity: pulseOpacity,
          transform: `scale(1, ${pulseSize / 100})`,
          transformOrigin: 'center',
          transition: 'all 2s ease-in-out'
        }} data-id="element-2465" />
        </svg>
      </div>
      {/* Left-side Circuit */}
      <div className="fixed left-12 top-0 bottom-0 w-1 bg-[#1A2331] z-10 pointer-events-none" data-id="element-2466">
        {/* Vertical circuit line */}
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-[#F5F7FA] bg-opacity-10" data-id="element-2467"></div>
        {/* Circuit nodes */}
        {circuitNodes.map((node, index) => <div key={index} className="absolute left-1/2 transform -translate-x-1/2" style={{
        top: `${(index + 1) * 15}%`
      }} data-id="element-2468">
            {/* Node circle */}
            <div className={`w-3 h-3 rounded-full transition-all duration-300 ${node.active ? 'bg-[#C8F169] shadow-[0_0_10px_2px_rgba(200,241,105,0.7)]' : 'bg-[#3B82F6] bg-opacity-40'}`} data-id="element-2469"></div>
            {/* Horizontal connection */}
            <div className={`absolute left-4 top-1.5 h-0.5 transition-all duration-500 ${node.active ? 'bg-[#C8F169] shadow-[0_0_5px_1px_rgba(200,241,105,0.7)]' : 'bg-[#F5F7FA] bg-opacity-10'}`} style={{
          width: node.active ? '40px' : '20px',
          transform: `translateY(-50%)`
        }} data-id="element-2470"></div>
            {/* Blast effect */}
            {node.active && <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#C8F169] opacity-0 animate-ping" data-id="element-2471"></div>}
          </div>)}
      </div>
      {/* Neural Network Background */}
      <div className="absolute inset-0 z-0 pointer-events-none" data-id="element-2472">
        {Array.from({
        length: 15
      }).map((_, i) => <div key={i} className={`absolute rounded-full transition-all duration-1000 ${neuronActive.includes(i) ? 'bg-[#C8F169]' : 'bg-[#3B82F6]'}`} style={{
        width: neuronActive.includes(i) ? '8px' : '4px',
        height: neuronActive.includes(i) ? '8px' : '4px',
        left: `${20 + i * 6}%`,
        top: `${15 + i % 5 * 15}%`,
        opacity: neuronActive.includes(i) ? 0.8 : 0.2,
        boxShadow: neuronActive.includes(i) ? '0 0 10px 2px rgba(200,241,105,0.6)' : 'none',
        filter: neuronActive.includes(i) ? 'blur(1px)' : 'none',
        transform: `translateY(${scrollY * 0.1}px)`
      }} data-id="element-2473"></div>)}
        {/* Neural connections */}
        <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" data-id="element-2474">
          <defs data-id="element-2475">
            <linearGradient id="neuronGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-2476">
              <stop offset="0%" stopColor="#3B82F6" data-id="element-2477" />
              <stop offset="100%" stopColor="#C8F169" data-id="element-2478" />
            </linearGradient>
          </defs>
          {Array.from({
          length: 20
        }).map((_, i) => <path key={i} d={`M${20 + i * 30},${50 + i % 5 * 20} Q${100 + i * 5},${150 + i % 3 * 40} ${200 + i * 20},${100 + i % 4 * 30}`} stroke="url(#neuronGradient)" strokeWidth="0.5" fill="none" strokeDasharray="5,5" style={{
          opacity: neuronActive.includes(i % 15) ? 0.6 : 0.1,
          transform: `translateY(${scrollY * 0.05}px)`
        }} data-id="element-2479" />)}
        </svg>
      </div>
      {/* Main Content */}
      <Header data-id="element-2480" />
      <main className="relative z-10" data-id="element-2481">
        <div ref={el => sectionRefs.current[0] = el} data-id="element-2482">
          <Hero data-id="element-2483" />
        </div>
        <div ref={el => sectionRefs.current[1] = el} data-id="element-2484">
          <FeatureShowcase data-id="element-2485" />
        </div>
        <div ref={el => sectionRefs.current[2] = el} data-id="element-2486">
          <Features data-id="element-2487" />
        </div>
        <div ref={el => sectionRefs.current[3] = el} data-id="element-2488">
          <Metrics data-id="element-2489" />
        </div>
        <div ref={el => sectionRefs.current[4] = el} data-id="element-2490">
          <GetStarted data-id="element-2491" />
        </div>
        <div ref={el => sectionRefs.current[5] = el} data-id="element-2492">
          <FAQ data-id="element-2493" />
        </div>
      </main>
      <Footer data-id="element-2494" />
      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none z-5" data-id="element-2495">
        {Array.from({
        length: 20
      }).map((_, i) => <div key={i} className="absolute rounded-full bg-[#C8F169]" style={{
        width: `${2 + i % 3}px`,
        height: `${2 + i % 3}px`,
        left: `${10 + i * 5}%`,
        top: `${Math.sin(Date.now() / 1000 + i) * 10 + 50}%`,
        opacity: 0.1 + i % 10 * 0.01,
        animation: `float ${5 + i % 5}s infinite ease-in-out`,
        animationDelay: `${i * 0.5}s`,
        boxShadow: '0 0 5px rgba(200,241,105,0.3)'
      }} data-id="element-2496"></div>)}
      </div>
      {/* Gradient Overlay */}
      <div className="fixed inset-0 pointer-events-none z-1 opacity-20" style={{
      background: 'radial-gradient(circle at 70% 30%, rgba(200,241,105,0.1), transparent 70%), radial-gradient(circle at 30% 70%, rgba(59,130,246,0.1), transparent 70%)'
    }} data-id="element-2497"></div>
      {/* Add a style tag for custom animations */}
      <style jsx data-id="element-2498">{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-20px) translateX(10px);
          }
        }
        @keyframes pulse-glow {
          0%,
          100% {
            filter: drop-shadow(0 0 5px rgba(200, 241, 105, 0.3));
          }
          50% {
            filter: drop-shadow(0 0 15px rgba(200, 241, 105, 0.7));
          }
        }
      `}</style>
    </div>;
}